import { Banner, StatusBadge } from '@immo/ui-kit';
import type { ReactNode } from 'react';
import type {
  FleetCommandView,
  FleetJobView,
  FleetLogLineStream,
  FleetLogReadLimit,
  ReadFleetLogResponse,
  FleetMachineOverview,
  FleetProfileCapacity,
} from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { useLocaleFormat } from '@/i18n/use-locale-format';
import { FleetApprovalBadge } from './fleet-badges';
import { Last24HoursCard } from './health/last-24-hours-card';
import { PanelHealth } from './health/panel-health';
import { FleetThrottleBadge } from './health/throttle-badge';
import { ProfileCard } from './profile-card';
import { LogViewer } from './log-viewer';
import { CommandActivity } from './command-activity';
import { JobPulse, SuspectNotice } from './job-pulse';

export type ViewerState = {
  readonly open: boolean;
  readonly stream: FleetLogLineStream;
  readonly limit: FleetLogReadLimit;
  /** What the last read returned, oldest first — `null` until the first read resolves. */
  readonly log: ReadFleetLogResponse | null;
  readonly loading: boolean;
};

type Props = {
  readonly machine: FleetMachineOverview;
  /** The lane the panel shows, or `null` for the machine itself (OPS-128). */
  readonly lane: string | null;
  readonly canCommand: boolean;
  /** The machine's commands, newest first — the lane view keeps that lane's own. */
  readonly commands: ReadonlyArray<FleetCommandView>;
  /** The open lane's log viewer; absent until the page has set it up. */
  readonly viewer?: ViewerState;
  /** Each lane's fleet-wide cap and the other machines running it, keyed by lane — absent for an uncapped lane. */
  readonly capacities?: Readonly<Record<string, FleetProfileCapacity>>;
  readonly onStreamChange: (profile: string, stream: FleetLogLineStream) => void;
  readonly onLimitChange: (profile: string, limit: FleetLogReadLimit) => void;
  readonly onRefreshViewer: (profile: string) => void;
  readonly onCleanLogs: (profile: string) => void;
  /** The fleet's live jobs (fleet-agent ADR 0012): the lane view shows the one its worker holds on this machine. */
  readonly jobs?: ReadonlyArray<FleetJobView>;
  /** After a suspect is cleared or its job released from the lane view. */
  readonly onJobsChanged?: () => void | Promise<void>;
};

/**
 * The side panel's content (OPS-128): details only. Every action — approval, host verbs, lane verbs, engine — sits on
 * its row in the table, so none is repeated here; what stays are the controls that belong to their content: the log
 * viewer's own, and a suspect job's. Claude accounts live on Fleet › Accounts, not per machine.
 *
 * `lane === null` is the machine view: badges, facts, health, banners, the machine's command history. A lane is the
 * lane view: its card (fields, worker, capacity, engine, live job) with its log open, and that lane's history.
 */
export function MachineDetail({ machine, lane, ...rest }: Props) {
  const profile = lane === null ? undefined : machine.profiles.find((entry) => entry.profile === lane);

  return profile === undefined ? (
    <MachineView machine={machine} commands={rest.commands} canCommand={rest.canCommand} />
  ) : (
    <LaneView machine={machine} profile={profile} {...rest} />
  );
}

function MachineView({
  machine,
  commands,
  canCommand,
}: {
  readonly machine: FleetMachineOverview;
  readonly commands: ReadonlyArray<FleetCommandView>;
  readonly canCommand: boolean;
}) {
  const t = useTranslations('pages.admin.fleet');
  const format = useLocaleFormat();
  const approved = machine.approval === 'approved';

  return (
    // One scroll context, the panel's own.
    <div className="flex flex-col gap-4 px-4 py-4 sm:px-6 sm:py-5">
      <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
        <FleetApprovalBadge approval={machine.approval} />
        {approved && machine.health?.throttle != null && <FleetThrottleBadge throttle={machine.health.throttle} />}
        {machine.stale && <StatusBadge label={t('stale')} tone="danger" dot />}
      </div>

      <MachineBanners machine={machine} canCommand={canCommand} />

      {/* Label/value pairs, one per line: the panel is 480px. Only facts about the HOST. */}
      <dl className="flex flex-col gap-1.5">
        {machineFacts(machine, t, format.ago).map(([label, value, display]) => (
          <div key={label} className="flex min-w-0 items-baseline gap-3">
            <dt className="w-32 shrink-0 text-sm text-text-tertiary">{label}</dt>
            <dd className="min-w-0 truncate font-mono text-base-sm text-text-primary" title={value}>
              {display ?? value}
            </dd>
          </div>
        ))}
      </dl>

      {approved && (
        <>
          {/* The disk report and prune moved to the row's More menu: no action buttons in the panel. */}
          <PanelHealth
            health={machine.health ?? null}
            canCommand={false}
            hostCommandPending={false}
            onSystemPreview={() => undefined}
            onSystemPrune={() => undefined}
          />
          <Last24HoursCard machineId={machine.id} health={machine.health ?? null} />
        </>
      )}

      <CommandActivity commands={commands} />
    </div>
  );
}

function LaneView({
  machine,
  profile,
  canCommand,
  commands,
  viewer,
  capacities = {},
  onStreamChange,
  onLimitChange,
  onRefreshViewer,
  onCleanLogs,
  jobs = [],
  onJobsChanged = () => undefined,
}: Omit<Props, 'lane'> & { readonly profile: FleetMachineOverview['profiles'][number] }) {
  const job =
    profile.worker == null
      ? undefined
      : jobs.find((candidate) => candidate.machineId === machine.id && candidate.worker === profile.worker);

  return (
    <div className="flex flex-col gap-4 px-4 py-4 sm:px-6 sm:py-5">
      <MachineBanners machine={machine} canCommand={canCommand} />
      <ProfileCard
        profile={profile}
        stale={machine.stale}
        capacity={capacities[profile.profile] ?? null}
        pulse={
          job === undefined ? undefined : (
            <div className="flex flex-col gap-2">
              <JobPulse job={job} className="text-sm" />
              <SuspectNotice job={job} canCommand={canCommand} onChanged={onJobsChanged} />
            </div>
          )
        }
      >
        {viewer?.open === true && (
          <LogViewer
            readOnly={!canCommand}
            stream={viewer.stream}
            limit={viewer.limit}
            log={viewer.log}
            loading={viewer.loading}
            onStreamChange={(stream) => onStreamChange(profile.profile, stream)}
            onLimitChange={(limit) => onLimitChange(profile.profile, limit)}
            onRefresh={() => onRefreshViewer(profile.profile)}
            {...(canCommand && profile.worker != null ? { onCleanLogs: () => onCleanLogs(profile.profile) } : {})}
          />
        )}
      </ProfileCard>
      <CommandActivity commands={commands.filter((command) => command.profile === profile.profile)} />
    </div>
  );
}

function MachineBanners({
  machine,
  canCommand,
}: {
  readonly machine: FleetMachineOverview;
  readonly canCommand: boolean;
}) {
  const t = useTranslations('pages.admin.fleet');
  const format = useLocaleFormat();

  return (
    <>
      {machine.approval === 'pending' && (
        <Banner variant="warning" title={t('join-request-title')} description={t('join-request-body')} />
      )}
      {machine.stale && (
        <Banner
          variant="danger"
          title={t('stale')}
          description={t('stale-note', { time: format.ago(machine.lastSeenAt) })}
        />
      )}
      {!canCommand && <Banner variant="info" title={t('read-only-note')} />}
    </>
  );
}

/** The machine's facts, in reading order — label, plain value, and an optional rendering of it; the approval only once reported. */
function machineFacts(
  machine: FleetMachineOverview,
  t: (key: string, values?: Record<string, string>) => string,
  ago: (iso: string) => string,
): ReadonlyArray<readonly [string, string, ReactNode?]> {
  return [
    [t('host'), machine.hostname],
    [t('platform'), machine.platform],
    [t('agent-version'), machine.agentVersion, <AgentRelease key="release" version={machine.agentVersion} />],
    [t('first-seen'), ago(machine.firstSeenAt)],
    [t('last-seen'), ago(machine.lastSeenAt)],
    ...(machine.approvedByName !== null && machine.approvedAt !== null
      ? [[t('approved-by'), `${machine.approvedByName} · ${ago(machine.approvedAt)}`] as const]
      : []),
    [t('public-key'), shortKey(machine.publicKey)],
  ];
}

/**
 * The installed fleet-agent release, linked to its GitHub release. From 0.21.0
 * the host reports it on every tick; an older host shows what it enrolled with.
 */
function AgentRelease({ version }: { readonly version: string }) {
  return (
    <a
      href={`${FLEET_AGENT_RELEASES_URL}/v${version}`}
      target="_blank"
      rel="noreferrer noopener"
      className="text-interactive hover:underline"
    >
      {version}
    </a>
  );
}

const FLEET_AGENT_RELEASES_URL = 'https://github.com/hlrealestate/fleet-agent/releases/tag';

/** The key's ends, enough to tell two machines apart; the whole key is never something an operator reads. */
function shortKey(key: string): string {
  return key.length <= 16 ? key : `${key.slice(0, 8)}…${key.slice(-8)}`;
}
